'use client';

import React, { Fragment, useEffect, useState } from 'react';
import { ApiRoutes } from '@/settings/routes';
import { FaCalendarDays, FaCircleChevronLeft, FaCircleChevronRight } from 'react-icons/fa6';
import Textarea from '@/components/ui/forms/Textarea';
import Input from '@/components/front/ui/Input';
import Success from '@/components/ui/alerts/Success';
import Errors from '@/components/ui/alerts/Errors';
import axios from '@/lib/axios';
import InputNumber from '@/components/ui/forms/InputNumber';
import Switch from '@/components/ui/forms/Switch';
import { differenceInDays, format } from 'date-fns';
import Label from '@/components/ui/forms/Label';
import InputError from '@/components/ui/alerts/InputError';
import Button from '@/components/ui/forms/Button';
import ButtonLoading from '@/components/ui/forms/ButtonLoading';
import { App_text } from '@/settings/AppText';
import { useSearchParams } from 'next/navigation';
import { HiChevronLeft, HiChevronRight, HiCheck } from 'react-icons/hi2';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
    return new Date(year, month, 1).getDay();
}

function isSameDay(a, b) {
    return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function formatDisplay(date) {
    if (!date) return '';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatISODate(date) {
    if (!date) return null;
    return new Date(date).toISOString().split('T')[0];
}

function SingleDatePopover({ selectedDate, minDate, onChange, onClose }) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const min = minDate ? new Date(new Date(minDate).setHours(0, 0, 0, 0)) : today;

    const [viewYear, setViewYear] = useState(selectedDate ? selectedDate.getFullYear() : min.getFullYear());
    const [viewMonth, setViewMonth] = useState(selectedDate ? selectedDate.getMonth() : min.getMonth());

    const prevMonth = () => {
        if (viewMonth === 0) {
            setViewMonth(11);
            setViewYear((y) => y - 1);
        } else setViewMonth((m) => m - 1);
    };

    const nextMonth = () => {
        if (viewMonth === 11) {
            setViewMonth(0);
            setViewYear((y) => y + 1);
        } else setViewMonth((m) => m + 1);
    };

    const daysInMonth = getDaysInMonth(viewYear, viewMonth);
    const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(viewYear, viewMonth, d));

    return (
        <>
            <div className="fixed inset-0 z-30" onClick={onClose} />
            <div className="absolute left-0 bottom-full z-40 p-5 mb-3 bg-white rounded-2xl border border-gray-100" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.15)', minWidth: '280px' }}>
                <div className="flex justify-between items-center mb-4">
                    <button type="button" onClick={prevMonth} className="flex justify-center items-center w-8 h-8 text-gray-500 rounded-lg transition-colors hover:bg-gray-100 hover:text-gray-800">
                        <HiChevronLeft className="size-5" />
                    </button>
                    <span className="text-sm font-bold text-gray-800">
                        {MONTHS[viewMonth]} {viewYear}
                    </span>
                    <button type="button" onClick={nextMonth} className="flex justify-center items-center w-8 h-8 text-gray-500 rounded-lg transition-colors hover:bg-gray-100 hover:text-gray-800">
                        <HiChevronRight className="size-5" />
                    </button>
                </div>

                <div className="grid grid-cols-7 mb-1">
                    {DAYS.map((d) => (
                        <div key={d} className="py-1 text-[0.65rem] font-bold text-center text-gray-400 uppercase">
                            {d}
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-7">
                    {cells.map((date, i) => {
                        if (!date) return <div key={`e-${i}`} />;
                        const isPast = date < min;
                        const isSelected = isSameDay(date, selectedDate);
                        const isToday = isSameDay(date, today);
                        return (
                            <button
                                key={date.toISOString()}
                                type="button"
                                disabled={isPast}
                                onClick={() => {
                                    onChange(date);
                                    onClose();
                                }}
                                className={`
                                    relative h-9 text-xs font-medium rounded-lg transition-all duration-150 select-none
                                    ${isPast ? 'text-gray-300 cursor-not-allowed' : 'cursor-pointer'}
                                    ${isSelected ? 'font-bold text-white' : ''}
                                    ${!isSelected && !isPast ? 'text-gray-700 hover:bg-orange-50 hover:text-main-color' : ''}
                                `}
                                style={{ backgroundColor: isSelected ? '#E8604C' : undefined }}
                            >
                                {isToday && !isSelected && <span className="absolute bottom-1 left-1/2 w-1 h-1 rounded-full -translate-x-1/2 bg-main-color" />}
                                {date.getDate()}
                            </button>
                        );
                    })}
                </div>
            </div>
        </>
    );
}

/* ─────────────────────────────────────────────
   STEPPER ITEM — styled like the services section
   active  → filled orange circle + glow
   done    → teal filled circle with ✓
   pending → white circle, gray border
───────────────────────────────────────────── */
function StepperItem({ step: s, currentStep, label, description, totalSteps }) {
    const isActive = s === currentStep;
    const isDone = s < currentStep;
    const isPending = s > currentStep;
    const isLast = s === totalSteps;

    /* icon inside the circle */
    const iconMap = {
        1: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                <rect x="9" y="3" width="6" height="4" rx="1" />
                <line x1="9" y1="12" x2="15" y2="12" />
                <line x1="9" y1="16" x2="13" y2="16" />
            </svg>
        ),
        2: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
        ),
        3: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                <circle cx="12" cy="7" r="4" />
            </svg>
        ),
        4: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 11 12 14 22 4" />
                <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
            </svg>
        ),
    };

    return (
        <li className="flex flex-col flex-1 items-center text-center group">
            {/* ── circle + connecting line row ── */}
            <div className="flex relative justify-center items-center w-full">
                {/* left line */}
                {s > 1 && (
                    <div
                        className="absolute right-1/2 top-[35px] h-[2px] hidden lg:block"
                        style={{
                            left: 0,
                            background: isDone || isActive ? 'linear-gradient(to right, rgba(232,96,76,0.3), #E8604C)' : '#e5e7eb',
                        }}
                    />
                )}
                {/* right line */}
                {!isLast && (
                    <div
                        className="absolute left-1/2 top-[35px] h-[2px] hidden lg:block"
                        style={{
                            right: 0,
                            background: isDone ? 'linear-gradient(to right, #E8604C, rgba(232,96,76,0.3))' : '#e5e7eb',
                        }}
                    />
                )}

                {/* ── Number badge (top-right of circle) ── */}
                <div className="relative z-10">
                    {!isDone && (
                        <div
                            className="absolute -top-2 -right-2 z-20 flex justify-center items-center w-6 h-6 rounded-full text-white text-[0.65rem] font-bold shadow-md"
                            style={{ backgroundColor: isActive ? '#E8604C' : '#d1d5db' }}
                        >
                            {s}
                        </div>
                    )}

                    {/* ── Main circle ── */}
                    <div
                        className="flex justify-center items-center w-[72px] h-[72px] rounded-full transition-all duration-300"
                        style={
                            isActive
                                ? {
                                      background: 'linear-gradient(135deg, #E8604C 0%, #313041 100%)',
                                      boxShadow: '0 6px 24px rgba(232,96,76,0.45)',
                                      transform: 'scale(1.1)',
                                  }
                                : isDone
                                  ? {
                                        background: 'linear-gradient(135deg, #313041 0%, #0d9488 100%)',
                                        boxShadow: '0 4px 16px rgba(13,148,136,0.3)',
                                    }
                                  : {
                                        backgroundColor: '#ffffff',
                                        border: '2px solid rgba(232,96,76,0.25)',
                                        boxShadow: '0 4px 16px rgba(232,96,76,0.08)',
                                    }
                        }
                    >
                        {isDone ? (
                            /* checkmark for done */
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" stroke="white">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        ) : (
                            /* icon for active / pending */
                            <svg
                                width="26"
                                height="26"
                                viewBox="0 0 24 24"
                                fill="none"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="transition-all duration-300"
                                stroke={isActive ? 'white' : '#E8604C'}
                            >
                                {iconMap[s]?.props?.children}
                            </svg>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Label & description ── */}
            <div className="px-2 mt-5">
                <span className="block text-sm font-bold transition-colors duration-200" style={{ color: isActive ? '#E8604C' : isDone ? '#0d9488' : '#6b7280' }}>
                    {label}
                </span>
                <p className="hidden mt-1 text-xs leading-relaxed text-gray-400 lg:block">{description}</p>
            </div>
        </li>
    );
}

export default function ContentTailorMadePage() {
    const searchParams = useSearchParams();
    const [error, setError] = useState('');
    const [errors, setErrors] = useState([]);
    const [success, setSuccess] = useState('');
    const [processing, setProcessing] = useState(false);
    const [countTravels, setCountTravels] = useState(0);
    const [step, setStep] = useState(1);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [showStartCal, setShowStartCal] = useState(false);
    const [showEndCal, setShowEndCal] = useState(false);
    const [formFields, setFormFields] = useState({
        name: '',
        subject: '',
        email: '',
        child: false,
        adulte: '0',
        ado: '0',
        childs: '0',
        baby: '0',
        slide: false,
        days: 0,
        from: null,
        to: null,
        accommodation: false,
        accommodation_field: '',
        transport: false,
        transport_field: '',
        activity: false,
        activity_field: '',
        budget: 0,
        festive_nightlife: false,
        calm_relaxing: false,
        cultural_historical: false,
        adventure_activities: false,
        message: '',
    });

    useEffect(() => {
        const start = searchParams.get('start');
        const end = searchParams.get('end');
        const adult = searchParams.get('adult');
        const youth = searchParams.get('youth');
        const children = searchParams.get('children');
        if (start || end || adult) {
            const from = start ? new Date(start) : null;
            const to = end ? new Date(end) : null;
            if (from) setStartDate(from);
            if (to) setEndDate(to);
            setFormFields((prev) => ({
                ...prev,
                adulte: adult || prev.adulte,
                ado: youth || prev.ado,
                childs: children || prev.childs,
                child: parseInt(youth || 0) > 0 || parseInt(children || 0) > 0,
            }));
        }
    }, [searchParams]);

    useEffect(() => {
        const totalTravels = parseInt(formFields.adulte) + parseInt(formFields.ado) + parseInt(formFields.childs) + parseInt(formFields.baby);
        setCountTravels(totalTravels);
    }, [formFields]);

    useEffect(() => {
        const daysDifference = startDate && endDate ? differenceInDays(new Date(endDate), new Date(startDate)) : 0;
        setFormFields((prev) => ({
            ...prev,
            from: startDate ? formatISODate(startDate) : null,
            to: endDate ? formatISODate(endDate) : null,
            days: Number.isFinite(daysDifference) ? daysDifference : 0,
        }));
    }, [startDate, endDate]);

    const handelInput = (e) => {
        e.persist();
        const { name, value } = e.target;
        setFormFields((prev) => ({ ...prev, [name]: value }));
    };

    const changeInput = (name, val) => {
        const newValue = Array.isArray(val) && val.length === 1 ? parseFloat(val[0]) : val;
        setFormFields((prev) => ({ ...prev, [name]: newValue }));
    };

    const handelCheckbox = (e) => {
        setFormFields((prev) => ({ ...prev, [e.target.name]: e.target.checked }));
    };

    const validateForm = () => {
        const newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formFields.name === '') newErrors.name = ['Name is required'];
        if (formFields.email === '') newErrors.email = ['Email is required'];
        else if (!emailRegex.test(formFields.email)) newErrors.email = ['Email is invalid'];
        if (formFields.subject === '') newErrors.subject = ['Subject is required'];
        if (formFields.adulte === '0') newErrors.adulte = ['Adulte is required'];
        if (formFields.days == 0) newErrors.days = ['Date is required'];
        if (formFields.budget === 0) newErrors.budget = ['Budget is required'];
        if (formFields.message === '') newErrors.message = ['Message is required'];
        if (Object.keys(newErrors).length === 0) setErrors([]);
        else setErrors(newErrors);
    };

    const submitForm = async (e) => {
        e.preventDefault();
        setProcessing(true);
        setError('');
        setErrors([]);
        const payload = {
            ...formFields,
            from: startDate ? formatISODate(startDate) : null,
            to: endDate ? formatISODate(endDate) : null,
            days: startDate && endDate ? differenceInDays(new Date(endDate), new Date(startDate)) : 0,
        };
        axios
            .post(ApiRoutes.API_create_estimate, payload)
            .then((res) => {
                setProcessing(false);
                if (res.data.status === 200) {
                    setSuccess(res.data.message);
                    setStartDate(null);
                    setEndDate(null);
                    setFormFields({
                        name: '',
                        subject: '',
                        email: '',
                        child: false,
                        adulte: '0',
                        ado: '0',
                        childs: '0',
                        baby: '0',
                        slide: false,
                        days: 0,
                        from: null,
                        to: null,
                        accommodation: false,
                        accommodation_field: '',
                        transport: false,
                        transport_field: '',
                        activity: false,
                        activity_field: '',
                        budget: 0,
                        festive_nightlife: false,
                        calm_relaxing: false,
                        cultural_historical: false,
                        adventure_activities: false,
                        message: '',
                    });
                    setStep(5);
                } else if (res.data.status === 401) {
                    setErrors(res.data.errors);
                    setSuccess('');
                    setError('');
                }
            })
            .catch((error) => {
                setProcessing(false);
                if (error.response?.status === 422) {
                    setError('whoops!! something went wrong , please try later');
                    setSuccess('');
                    setErrors([]);
                }
            });
    };

    const handleNext = () => {
        setErrors([]);
        let err = 0;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (step === 1) {
            if (formFields.adulte == '0' || formFields.days == 0 || formFields.budget == '0' || formFields.subject == '') {
                validateForm();
                err = 1;
            }
        }
        if (step === 2) {
            if (formFields.message === '') {
                validateForm();
                err = 1;
            }
        }
        if (step === 3) {
            if (formFields.name === '' || formFields.email === '' || !emailRegex.test(formFields.email)) {
                validateForm();
                err = 1;
            }
        }
        if (err === 0) setStep((prevStep) => prevStep + 1);
    };

    const handleBack = () => setStep((prevStep) => prevStep - 1);

    return (
        <form onSubmit={submitForm}>
            {/* ══════════════════════════════════════
                NEW STEPPER — services section style
            ══════════════════════════════════════ */}
            <ul className="flex relative gap-2 mx-auto">
                <StepperItem step={1} currentStep={step} totalSteps={4} label={App_text.pages.tailor_made.tabs.titls_1} description={App_text.pages.tailor_made.tabs.short_description_1} />
                <StepperItem step={2} currentStep={step} totalSteps={4} label={App_text.pages.tailor_made.tabs.titls_2} description={App_text.pages.tailor_made.tabs.short_description_2} />
                <StepperItem step={3} currentStep={step} totalSteps={4} label={App_text.pages.tailor_made.tabs.titls_3} description={App_text.pages.tailor_made.tabs.short_description_3} />
                <StepperItem step={4} currentStep={step} totalSteps={4} label={App_text.pages.tailor_made.tabs.titls_4} description={App_text.pages.tailor_made.tabs.short_description_4} />
            </ul>

            <div className="mt-8 sm:mt-10">
                {/* ── Step 1 ── */}
                <div className={`${step !== 1 && 'hidden'} p-4 rounded-xl bg-gray-50 border-dashed border border-gray-200 dark:border-neutral-700`}>
                    <div className="flex flex-col gap-2 sm:p-4 lg:p-10">
                        <div className="flex flex-col gap-2">
                            <div className="col-span-12 lg:col-span-5">
                                <p className="my-4 text-base font-normal text-gray-600">{App_text.pages.tailor_made.description_tab_2}</p>
                            </div>
                            <div className="col-span-12 p-4 bg-white rounded-2xl border border-gray-200 lg:col-span-7 lg:p-8 dark:border-neutral-700">
                                <div className="w-full">
                                    <Label htmlFor="name">Subject</Label>
                                    <Input
                                        onChange={handelInput}
                                        defaultValue={formFields.subject}
                                        name="subject"
                                        type="text"
                                        placeholder="Title of stay (Weekend in Nice, Tour in Florida, etc.)"
                                        className={'text-base rounded-lg focus:!border-main-color focus:!ring-main-color'}
                                    />
                                    {errors.subject && errors.subject.map((error, index) => <InputError key={index} message={error} />)}
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <div className="col-span-12 lg:col-span-5">
                                <p className="my-4 text-base font-normal text-gray-600">{App_text.pages.tailor_made.description_tab_3}</p>
                            </div>
                            <div className="col-span-12 p-4 bg-white rounded-2xl border border-gray-200 lg:col-span-7 lg:p-8 dark:border-neutral-700">
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-center lg:gap-6">
                                    <div className="flex flex-col w-full">
                                        <Label htmlFor="adulte">Adults*</Label>
                                        <InputNumber id={'adulte'} defaultValue={formFields?.adulte} changeInput={(val) => changeInput('adulte', val)} required />
                                        {errors.adulte && errors.adulte.map((error, index) => <InputError key={index} message={error} />)}
                                    </div>
                                    <div className="my-5 w-full sm:mt-5 sm:mb-0">
                                        <Switch lable={'Child < 18 years old ?'} name={'child'} onChange={handelCheckbox} checked={formFields?.child} />
                                    </div>
                                    <div className={`${formFields?.child === false && 'hidden'} w-full sm:col-span-2`}>
                                        <Label htmlFor="ado">Teenagers (12 to 17 years old)</Label>
                                        <InputNumber id={'ado'} defaultValue={formFields?.ado} changeInput={(val) => changeInput('ado', val)} />
                                    </div>
                                    <div className={`${formFields?.child === false && 'hidden'} w-full sm:col-span-2`}>
                                        <Label htmlFor="childs">Children (2 to 11 years old)</Label>
                                        <InputNumber id={'childs'} defaultValue={formFields?.childs} changeInput={(val) => changeInput('childs', val)} />
                                    </div>
                                    <div className={`${formFields?.child === false && 'hidden'} w-full sm:col-span-2`}>
                                        <Label htmlFor="baby">Baby {'(< 2 years)'}</Label>
                                        <InputNumber id={'baby'} defaultValue={formFields?.baby} changeInput={(val) => changeInput('baby', val)} />
                                    </div>
                                </div>
                                <h5 className="mt-6 text-base font-normal text-gray-600">
                                    Participants : <span className="font-semibold text-gray-800">{countTravels} Persons</span>
                                </h5>
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <div className="col-span-12 lg:col-span-5">
                                <p className="my-4 text-base font-normal text-gray-600">{App_text.pages.tailor_made.description_tab_4}</p>
                            </div>
                            <div className="col-span-12 p-4 bg-white rounded-2xl border border-gray-200 lg:col-span-7 lg:p-8 dark:border-neutral-700">
                                <div className="flex flex-col gap-4">
                                    <div className="flex flex-col gap-3 p-4 rounded-2xl border border-gray-200 bg-white/95 lg:flex-row lg:items-center lg:gap-0 lg:p-2">
                                        <div className="relative flex-1">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowStartCal(!showStartCal);
                                                    setShowEndCal(false);
                                                }}
                                                className="flex flex-col gap-1 px-4 py-3 w-full text-left rounded-xl transition-colors hover:bg-gray-50 lg:border-r lg:border-gray-200 lg:rounded-none"
                                            >
                                                <span className="text-xs font-semibold tracking-wide text-gray-500 uppercase">Start Date</span>
                                                <div className="flex gap-2 items-center">
                                                    <FaCalendarDays className="text-main-color size-4 shrink-0" />
                                                    <span className={`text-sm font-medium ${startDate ? 'text-gray-800' : 'text-gray-400'}`}>{startDate ? formatDisplay(startDate) : 'Add date'}</span>
                                                </div>
                                            </button>
                                            {showStartCal && (
                                                <SingleDatePopover
                                                    selectedDate={startDate}
                                                    minDate={null}
                                                    onChange={(date) => {
                                                        setStartDate(date);
                                                        if (endDate && date > endDate) setEndDate(null);
                                                    }}
                                                    onClose={() => setShowStartCal(false)}
                                                />
                                            )}
                                        </div>
                                        <div className="relative flex-1">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowEndCal(!showEndCal);
                                                    setShowStartCal(false);
                                                }}
                                                className="flex flex-col gap-1 px-4 py-3 w-full text-left rounded-xl transition-colors hover:bg-gray-50 lg:rounded-none"
                                            >
                                                <span className="text-xs font-semibold tracking-wide text-gray-500 uppercase">End Date</span>
                                                <div className="flex gap-2 items-center">
                                                    <FaCalendarDays className="text-main-color size-4 shrink-0" />
                                                    <span className={`text-sm font-medium ${endDate ? 'text-gray-800' : 'text-gray-400'}`}>{endDate ? formatDisplay(endDate) : 'Add date'}</span>
                                                </div>
                                            </button>
                                            {showEndCal && (
                                                <SingleDatePopover
                                                    selectedDate={endDate}
                                                    minDate={startDate ? new Date(startDate) : new Date()}
                                                    onChange={(date) => setEndDate(date)}
                                                    onClose={() => setShowEndCal(false)}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    {errors.days && errors.days.map((error, index) => <InputError key={index} message={error} />)}
                                    <h5 className="text-base font-normal text-gray-600">
                                        Length of stay : <span className={`text-gray-800 font-semibold ${Number(formFields.days) === 0 ? 'text-red-500' : ''}`}>
                                            {Number(formFields.days) > 0 ? `${Number(formFields.days) + 1} days/ ${Number(formFields.days)} nights` : `${Number(formFields.days)} night`}
                                        </span>
                                    </h5>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <div className="col-span-12 lg:col-span-5">
                                <p className="my-4 text-base font-normal text-gray-600">{App_text.pages.tailor_made.description_tab_6}</p>
                            </div>
                            <div className="col-span-12 p-4 bg-white rounded-2xl border border-gray-200 lg:col-span-7 lg:p-8 dark:border-neutral-700">
                                <div className="flex flex-col gap-4 lg:gap-6">
                                    <Switch
                                        lable={formFields?.accommodation ? 'With accommodation' : 'Without accommodation'}
                                        name={'accommodation'}
                                        onChange={handelCheckbox}
                                        defaultChecked={formFields?.accommodation}
                                    />
                                    {formFields.accommodation && (
                                        <Fragment>
                                            <Textarea
                                                rows={5}
                                                onChange={handelInput}
                                                defaultValue={formFields.accommodation_field}
                                                name="accommodation_field"
                                                type="text"
                                                placeholder="Please fill in the type of accommodation, board, room composition and other details here."
                                                className={'text-base rounded-lg focus:!border-main-color focus:!ring-main-color'}
                                            />
                                            {errors.accommodation_field && errors.accommodation_field.map((error, index) => <InputError key={index} message={error} />)}
                                        </Fragment>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <div className="col-span-12 lg:col-span-5">
                                <p className="my-4 text-base font-normal text-gray-600">{App_text.pages.tailor_made.description_tab_7}</p>
                            </div>
                            <div className="col-span-12 p-4 bg-white rounded-2xl border border-gray-200 lg:col-span-7 lg:p-8 dark:border-neutral-700">
                                <div className="flex flex-col gap-4 lg:gap-6">
                                    <Switch
                                        lable={formFields?.transport ? 'With transport' : 'Without transport'}
                                        name={'transport'}
                                        onChange={handelCheckbox}
                                        defaultChecked={formFields?.transport}
                                    />
                                    {formFields.transport && (
                                        <Fragment>
                                            <Textarea
                                                rows={5}
                                                onChange={handelInput}
                                                defaultValue={formFields.transport_field}
                                                name="transport_field"
                                                type="text"
                                                placeholder="Do you want a rental vehicle, a private transfer, a train or bus ticket? Thank you for mentioning it."
                                                className={'text-base rounded-lg focus:!border-main-color focus:!ring-main-color'}
                                            />
                                            {errors.transport_field && errors.transport_field.map((error, index) => <InputError key={index} message={error} />)}
                                        </Fragment>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <div className="col-span-12 lg:col-span-5">
                                <p className="my-4 text-base font-normal text-gray-600">{App_text.pages.tailor_made.description_tab_8}</p>
                            </div>
                            <div className="col-span-12 p-4 bg-white rounded-2xl border border-gray-200 lg:col-span-7 lg:p-8 dark:border-neutral-700">
                                <div className="flex flex-col gap-4 lg:gap-6">
                                    <Switch lable={formFields?.activity ? 'With activity' : 'Without activity'} name={'activity'} onChange={handelCheckbox} defaultChecked={formFields?.activity} />
                                    {formFields.activity && (
                                        <Fragment>
                                            <Textarea
                                                rows={5}
                                                onChange={handelInput}
                                                defaultValue={formFields.activity_field}
                                                name="activity_field"
                                                type="text"
                                                placeholder="Excursions, private tours, guides, entrance tickets,..."
                                                className={'text-base rounded-lg focus:!border-main-color focus:!ring-main-color'}
                                            />
                                            {errors.activity_field && errors.activity_field.map((error, index) => <InputError key={index} message={error} />)}
                                        </Fragment>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <div className="col-span-12 lg:col-span-5">
                                <p className="my-4 text-base font-normal text-gray-600">{App_text.pages.tailor_made.description_tab_9}</p>
                            </div>
                            <div className="col-span-12 p-4 bg-white rounded-2xl border border-gray-200 lg:col-span-7 lg:p-8 dark:border-neutral-700">
                                <div className="w-full">
                                    <Label htmlFor="name">Budget* (EUR)</Label>
                                    <Input
                                        onChange={(e) => {
                                            const value = Math.max(0, parseInt(e.target.value) || 0);
                                            e.target.value = value;
                                            handelInput(e);
                                        }}
                                        defaultValue={formFields.budget}
                                        min={0}
                                        name="budget"
                                        type="number"
                                        placeholder="Budget (EUR)"
                                        className={'text-base rounded-lg focus:!border-main-color focus:!ring-main-color'}
                                        onBlur={(e) => {
                                            if (e.target.value < 0 || e.target.value === '') {
                                                e.target.value = 0;
                                                const event = { ...e, persist: () => {} };
                                                handelInput(event);
                                            }
                                        }}
                                    />
                                    {errors.budget && errors.budget.map((error, index) => <InputError key={index} message={error} />)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Step 2 ── */}
                <div className={`${step !== 2 && 'hidden'} p-4 bg-gray-50 border border-dashed border-gray-200 rounded-xl dark:bg-neutral-800 dark:border-neutral-700`}>
                    <div className="flex flex-col gap-2 sm:p-4 lg:p-10">
                        <div className="flex flex-col gap-2">
                            <div className="col-span-12 lg:col-span-5">
                                <p className="my-4 text-base font-normal text-gray-600">{App_text.pages.tailor_made.description_tab_10}</p>
                            </div>
                            <div className="col-span-12 p-4 bg-white rounded-2xl border border-gray-200 lg:col-span-7 lg:p-8 dark:border-neutral-700">
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:gap-6">
                                    <div>
                                        <Switch lable={'Festive, nightlife'} name={'festive_nightlife'} onChange={handelCheckbox} defaultChecked={formFields?.festive_nightlife} />
                                    </div>
                                    <div>
                                        <Switch lable={'Calm, relaxing'} name={'calm_relaxing'} onChange={handelCheckbox} defaultChecked={formFields?.calm_relaxing} />
                                    </div>
                                    <div>
                                        <Switch lable={'Cultural, historical'} name={'cultural_historical'} onChange={handelCheckbox} defaultChecked={formFields?.cultural_historical} />
                                    </div>
                                    <div>
                                        <Switch lable={'Adventure, activities'} name={'adventure_activities'} onChange={handelCheckbox} defaultChecked={formFields?.adventure_activities} />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <div className="col-span-12 lg:col-span-5">
                                <p className="my-4 text-base font-normal text-gray-600">{App_text.pages.tailor_made.description_tab_11}</p>
                            </div>
                            <div className="col-span-12 p-4 bg-white rounded-2xl border border-gray-200 lg:col-span-7 lg:p-8 dark:border-neutral-700">
                                <div className="flex flex-col gap-4 lg:gap-6">
                                    <Label htmlFor="name">Message*</Label>
                                    <Textarea
                                        rows={6}
                                        onChange={handelInput}
                                        defaultValue={formFields.message}
                                        name="message"
                                        type="text"
                                        placeholder="Message"
                                        className={'text-base rounded-lg focus:!border-main-color focus:!ring-main-color'}
                                    />
                                    {errors.message && errors.message.map((error, index) => <InputError key={index} message={error} />)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Step 3 ── */}
                <div className={`${step !== 3 && 'hidden'} p-4 rounded-xl bg-gray-50 border-dashed border border-gray-200 dark:border-neutral-700`}>
                    <div className="flex flex-col gap-2 sm:p-4 lg:p-10">
                        <div className="col-span-12 lg:col-span-5">
                            <p className="my-4 text-base font-normal text-gray-600">{App_text.pages.tailor_made.description_tab_1}</p>
                        </div>
                        <div className="col-span-12 p-4 bg-white rounded-2xl border border-gray-200 lg:col-span-7 lg:p-8 dark:border-neutral-700">
                            <div className="grid grid-cols-1 gap-4 items-start sm:grid-cols-2 lg:gap-6">
                                <div className="w-full">
                                    <Label htmlFor="name">Full Name*</Label>
                                    <Input
                                        onChange={handelInput}
                                        defaultValue={formFields.name}
                                        name="name"
                                        type="text"
                                        placeholder="Full Name*"
                                        className={'text-base rounded-lg focus:!border-main-color focus:!ring-main-color'}
                                        required
                                    />
                                    {errors.name && errors.name.map((error, index) => <InputError key={index} message={error} />)}
                                </div>
                                <div className="w-full">
                                    <Label htmlFor="email">Email*</Label>
                                    <Input
                                        onChange={handelInput}
                                        defaultValue={formFields.email}
                                        name="email"
                                        type="email"
                                        placeholder="Email Address*"
                                        className={'text-base rounded-lg focus:!border-main-color focus:!ring-main-color'}
                                        required
                                    />
                                    {errors.email && errors.email.map((error, index) => <InputError key={index} message={error} />)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Step 4 ── */}
                <div className={`${step !== 4 && 'hidden'} p-4 bg-gray-50 border border-dashed border-gray-200 rounded-xl dark:bg-neutral-800 dark:border-neutral-700`}>
                    <Errors errors={errors} />
                    <div className="flex flex-col gap-2 sm:p-4 lg:p-10">
                        <div className="flex flex-col gap-2">
                            <div className="col-span-12 lg:col-span-5">
                                <p className="my-4 text-base font-normal text-gray-600">{App_text.pages.tailor_made.description_tab_12}</p>
                            </div>
                            <div className="col-span-12 p-4 bg-white rounded-2xl border border-gray-200 lg:col-span-7 lg:p-8 dark:border-neutral-700">
                                <div className="text-base font-normal text-gray-600">
                                    <div className="grid grid-cols-12 gap-4 p-4">
                                        <h5 className="col-span-12 font-semibold text-gray-700 sm:col-span-4">Contact</h5>
                                        <div className="col-span-12 sm:col-span-8">{formFields.name}</div>
                                    </div>
                                    <hr />
                                    <div className="grid grid-cols-12 gap-4 p-4">
                                        <h5 className="col-span-12 font-semibold text-gray-700 sm:col-span-4">Email</h5>
                                        <div className="col-span-12 sm:col-span-8">{formFields.email}</div>
                                    </div>
                                    <hr />
                                    <div className="grid grid-cols-12 gap-4 p-4">
                                        <h5 className="col-span-12 font-semibold text-gray-700 sm:col-span-4">Title of stay</h5>
                                        <div className="col-span-12 sm:col-span-8">{formFields.subject}</div>
                                    </div>
                                    <hr />
                                    <div className="grid grid-cols-12 gap-4 p-4">
                                        <h5 className="col-span-12 font-semibold text-gray-700 sm:col-span-4">Participants</h5>
                                        <div className="col-span-12 sm:col-span-8">
                                            <h6 className="font-semibold">{countTravels} participants</h6>
                                            <ul className="pl-4 list-disc md:pl-0">
                                                <li>{formFields?.adulte} adultes</li>
                                                {formFields?.child && (
                                                    <Fragment>
                                                        <li>{formFields?.ado} Teenagers</li>
                                                        <li>{formFields?.childs} Children</li>
                                                        <li>{formFields?.baby} Baby</li>
                                                    </Fragment>
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                    <hr />
                                    <div className="grid grid-cols-12 gap-4 p-4">
                                        <h5 className="col-span-12 font-semibold text-gray-700 sm:col-span-4">Dates</h5>
                                        <div className="col-span-12 sm:col-span-8">
                                            {startDate && endDate && (
                                                <h6 className="font-semibold">
                                                    {Number(formFields.days) + 1} days/ {Number(formFields.days)} nights,
                                                    <br />
                                                    between {format(startDate, 'MM-dd-yyyy')} and {format(endDate, 'MM-dd-yyyy')}
                                                </h6>
                                            )}
                                        </div>
                                    </div>
                                    <hr />
                                    <div className="grid grid-cols-12 gap-4 p-4">
                                        <h5 className="col-span-12 font-semibold text-gray-700 sm:col-span-4">Accommodation</h5>
                                        <div className="col-span-12 sm:col-span-8">
                                            {formFields.accommodation == true ? 'Yes' : 'No'}, {formFields.accommodation_field}
                                        </div>
                                    </div>
                                    <hr />
                                    <div className="grid grid-cols-12 gap-4 p-4">
                                        <h5 className="col-span-12 font-semibold text-gray-700 sm:col-span-4">Transport</h5>
                                        <div className="col-span-12 sm:col-span-8">
                                            {formFields.transport == true ? 'Yes' : 'No'}, {formFields.transport_field}
                                        </div>
                                    </div>
                                    <hr />
                                    <div className="grid grid-cols-12 gap-4 p-4">
                                        <h5 className="col-span-12 font-semibold text-gray-700 sm:col-span-4">Activities</h5>
                                        <div className="col-span-12 sm:col-span-8">
                                            {formFields.activity == true ? 'Yes' : 'No'}, {formFields.activity_field}
                                        </div>
                                    </div>
                                    <hr />
                                    <div className="grid grid-cols-12 gap-4 p-4">
                                        <h5 className="col-span-12 font-semibold text-gray-700 sm:col-span-4">Budget</h5>
                                        <div className="col-span-12 sm:col-span-8">
                                            {formFields.budget}
                                            {App_text.generale.currancy}
                                        </div>
                                    </div>
                                    <hr />
                                    <div className="grid grid-cols-12 gap-4 p-4">
                                        <h5 className="col-span-12 font-semibold text-gray-700 sm:col-span-4">Thème</h5>
                                        <div className="col-span-12 sm:col-span-8">
                                            {formFields.festive_nightlife && 'Festif, vie nocturne. '}
                                            {formFields.calm_relaxing && 'Calme, relaxant. '}
                                            {formFields.cultural_historical && 'Culturel, historique. '}
                                            {formFields.adventure_activities && 'Aventure, activités.'}
                                        </div>
                                    </div>
                                    <hr />
                                    <div className="grid grid-cols-12 gap-4 p-4">
                                        <h5 className="col-span-12 font-semibold text-gray-700 sm:col-span-4">Message</h5>
                                        <div className="col-span-12 sm:col-span-8">{formFields.message}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Step 5: Success ── */}
                <div className={`${step !== 5 && 'hidden'} p-4 bg-gray-50 border border-dashed border-gray-200 rounded-xl dark:bg-neutral-800 dark:border-neutral-700`}>
                    <div className="flex justify-center items-center">
                        <Success message={success} />
                    </div>
                </div>

                {/* ══════════════════════════════════════
                    FOOTER BUTTONS — Pill gradient style
                ══════════════════════════════════════ */}
                <div className="flex gap-x-3 justify-end items-center mt-6">
                    {/* ── Back button: Outline Gradient Border pill ── */}
                    {step > 1 && step < 5 && (
                        <button
                            type="button"
                            onClick={handleBack}
                            className="inline-flex gap-2 items-center px-6 py-3 text-sm font-semibold rounded-full border-none transition-all duration-200 cursor-pointer"
                            style={{
                                background: 'white',
                                color: '#313041',
                                padding: '11px 24px',
                                /* gradient border trick via outline + box-shadow won't work on pill,
                                   so we use a wrapper approach with pseudo — replicated inline via background-clip */
                                backgroundImage: 'none',
                                boxShadow: 'inset 0 0 0 2px transparent',
                                outline: 'none',
                                position: 'relative',
                            }}
                            // We achieve the gradient border via a wrapping span below
                        >
                            {/* gradient border ring */}
                            <span
                                aria-hidden="true"
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    borderRadius: '9999px',
                                    padding: '2px',
                                    background: 'linear-gradient(135deg, #E8604C, #313041)',
                                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                    WebkitMaskComposite: 'xor',
                                    maskComposite: 'exclude',
                                    pointerEvents: 'none',
                                }}
                            />
                            <HiChevronLeft className="size-4" />
                            <span>{App_text.pages.tailor_made.button_back}</span>
                        </button>
                    )}

                    {/* ── Next button: Orange → Dark pill ── */}
                    {step < 4 && (
                        <button
                            type="button"
                            onClick={handleNext}
                            className="inline-flex gap-2 items-center px-6 py-3 text-sm font-semibold text-white rounded-full border-none transition-all duration-200 cursor-pointer"
                            style={{
                                background: 'linear-gradient(135deg, #E8604C 0%, #313041 100%)',
                                boxShadow: '0 4px 16px rgba(232,96,76,0.35)',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = '0 6px 24px rgba(232,96,76,0.5)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = '0 4px 16px rgba(232,96,76,0.35)';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}
                        >
                            <span>{App_text.pages.tailor_made.button_next}</span>
                            <HiChevronRight className="size-4" />
                        </button>
                    )}

                    {/* ── Submit button: Orange → Dark pill ── */}
                    {errors.length === 0 &&
                        step === 4 &&
                        (processing === false ? (
                            <button
                                type="submit"
                                className="inline-flex gap-2 items-center px-6 py-3 text-sm font-semibold text-white rounded-full border-none transition-all duration-200 cursor-pointer"
                                style={{
                                    background: 'linear-gradient(135deg, #E8604C 0%, #313041 100%)',
                                    boxShadow: '0 4px 16px rgba(232,96,76,0.35)',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = '0 6px 24px rgba(232,96,76,0.5)';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(232,96,76,0.35)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                            >
                                <span>{App_text.pages.tailor_made.button_submit}</span>
                                <HiChevronRight className="size-4" />
                            </button>
                        ) : (
                            <div
                                className="inline-flex gap-2 justify-center items-center px-6 py-3 text-sm font-semibold text-white rounded-full opacity-80 cursor-not-allowed"
                                style={{ background: 'linear-gradient(135deg, #E8604C 0%, #313041 100%)', minWidth: '144px' }}
                            >
                                {/* spinner */}
                                <svg className="text-white animate-spin size-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                <span>Sending...</span>
                            </div>
                        ))}
                </div>
            </div>
        </form>
    );
}
